import { formatNullCellValue } from "../../../components/data-table/utils";
import { AssetTableData } from "../../../hooks/use-table-context/asset/types";
import { TicketTableData } from "@hooks/use-table-context/ticket/types";
import { WpFieldConfig } from "@hooks/use-config/types";
import { WpGenericPost, WpTicketMetaBox } from "@hooks/use-get/tickets/types"
import { GenericWpAsset, WpAsset } from "@hooks/use-get/assets/types";
import type { SiteUser } from "@hooks/use-get/users/types";
import type { AggregateAssetResult } from "@hooks/use-get/aggregate-assets";

/**
 * Same insight shape as the per-item counters below, but built from
 * server-aggregated {value, count} rows (see useGetAggregateAssets) instead
 * of counting client-side over a single fetched page — works identically
 * for Assets, Tickets, and Users since the aggregate response shape is the
 * same regardless of post type.
 */
export const getInsightsFromAggregateResult = (
  column: string,
  data: AggregateAssetResult[],
  fieldConfig?: Pick<WpFieldConfig, "options">
) => {
  return data.map((item, i) => {
    const value = item[column];
    return {
      value,
      count: Number(item.count ?? 0),
      id: `${column}-${i}`,
      key: column,
      label: formatLabel(
        ((fieldConfig?.options as unknown as any[]) ?? [])?.find?.(
          (option) => option.value === value
        )?.label || String(value)
      ),
    };
  });
};

export const getInsightsFromTableColumn = (
  column: keyof WpAsset,
  tableData: AssetTableData,
  getValue: (asset: GenericWpAsset) => string | number | boolean,
  assetsConfig: WpFieldConfig
) => {
  const columnValues = tableData.map((asset) => getValue?.(asset));
  const uniqueValues = Array.from(new Set(columnValues));
  const insights = uniqueValues.map((value, i) => ({
    value,
    count: columnValues.filter((v) => v === value).length,
    id: `${column}-${i}`,
    key: column,
    label: formatLabel(
      ((assetsConfig?.options as unknown as any[]) ?? [])?.find?.(
        (option) => option.value === value
      )?.label || String(value)
    ),
  }));

  return insights;
};

export const getTicketInsightsFromTableColumn = (
  column: keyof WpTicketMetaBox,
  tableData: TicketTableData,
  getValue: (asset: WpGenericPost<WpTicketMetaBox>) => string | number | boolean,
  assetsConfig: WpFieldConfig
) => {
  const columnValues = tableData.map((asset) =>
    formatNullCellValue(getValue?.(asset))
  );
  const uniqueValues = Array.from(new Set(columnValues));
  const insights = uniqueValues.map((value, i) => ({
    value,
    count: columnValues.filter((v) => v === value).length,
    id: `${column}-${i}`,
    key: column,
    label: formatLabel(
      ((assetsConfig?.options as unknown as any[]) ?? [])?.find?.(
        (option) => option.value === value
      )?.label || String(value)
    ),
  }));

  return insights;
}

export const getUserInsightsFromTableColumn = (
  column: keyof SiteUser,
  tableData: SiteUser[],
  getValue: (user: SiteUser) => string | number | boolean,
  usersConfig: WpFieldConfig
) => {
  const columnValues = tableData.map((user) =>
    formatNullCellValue(getValue?.(user))
  );
  const uniqueValues = Array.from(new Set(columnValues));
  const insights = uniqueValues.map((value, i) => ({
    value,
    count: columnValues.filter((v) => v === value).length,
    id: `${column}-${i}`,
    key: column,
    label: formatLabel(
      ((usersConfig?.options as unknown as any[]) ?? [])?.find?.(
        (option) => option.value === value
      )?.label || String(value)
    ),
  }));

  return insights;
};

export const formatLabel = (value?: string | number) => {
  if (value === undefined || value === "" || value === null) return "Undefined";
  if (typeof value !== "string") return value.toLocaleString();
  return value;
};
